<template>
  <div class="pagination-wrapper" data-test-id="table-pagination">
    <nav :aria-label="ariaLabel">
      <span class="sr-only" aria-live="polite" aria-atomic="true">
        {{ liveMessage }}
      </span>
      <ul :class="computedClasses">
        <li v-if="doubleArrows" :class="backArrowsClasses">
          <button
            type="button"
            class="page-link"
            @click="setPage(1)"
            :disabled="activePage === 1"
            aria-label="Go to first page"
            :aria-disabled="activePage === 1"
          >
            <i class="ri-arrow-left-double-line" aria-hidden="true"></i>
          </button>
        </li>
        <li v-if="arrows" :class="backArrowsClasses">
          <button
            type="button"
            class="page-link"
            @click="setPage(activePage - 1)"
            :disabled="activePage === 1"
            aria-label="Go to previous page"
            :aria-disabled="activePage === 1"
          >
            <i class="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
        </li>
        <li v-if="beforeDots" class="page-item disabled" aria-hidden="true">
          <span class="page-link">…</span>
        </li>

        <li
          v-for="item in items"
          :key="item"
          :class="[{ active: activePage === item }, 'page-item']"
        >
          <button
            type="button"
            class="page-link c-page-link-number"
            :data-page="item"
            @click="setPage(item, true)"
            :disabled="activePage === item"
            :aria-label="getPageAriaLabel(item)"
            :aria-disabled="activePage === item"
            :aria-current="activePage === item ? 'page' : undefined"
          >
            {{ item }}
          </button>
        </li>

        <li v-if="afterDots" class="page-item disabled" aria-hidden="true">
          <span class="page-link">…</span>
        </li>
        <li v-if="arrows" :class="nextArrowsClasses">
          <button
            type="button"
            class="page-link"
            @click="setPage(activePage + 1)"
            :disabled="activePage === pages"
            aria-label="Go to next page"
            :aria-disabled="activePage === pages"
          >
            <i class="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </li>
        <li v-if="doubleArrows" :class="nextArrowsClasses">
          <button
            type="button"
            class="page-link"
            @click="setPage(pages)"
            :disabled="activePage === pages"
            aria-label="Go to last page"
            :aria-disabled="activePage === pages"
          >
            <i class="ri-arrow-right-double-line" aria-hidden="true"></i>
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    activePage: {
      type: Number,
      default: 1
    },
    pages: {
      type: Number,
      default: 10
    },
    size: {
      type: String,
      validator: (val) => ["", "sm", "lg"].includes(val)
    },
    align: {
      type: String,
      default: "start",
      validator: (val) => ["start", "center", "end"].includes(val)
    },
    limit: {
      type: Number,
      default: 5
    },
    dots: {
      type: Boolean,
      default: true
    },
    arrows: {
      type: Boolean,
      default: true
    },
    doubleArrows: {
      type: Boolean,
      default: true
    },
    ariaLabel: {
      type: String,
      default: "Table pagination"
    }
  },
  emits: ["update:activePage", "update:modelValue"],
  data() {
    return {
      liveMessage: ""
    }
  },
  computed: {
    backArrowsClasses() {
      return ["page-item", { disabled: this.activePage === 1 }]
    },
    nextArrowsClasses() {
      return ["page-item", { disabled: this.activePage === this.pages }]
    },
    computedClasses() {
      const sizeClass = this.size ? `pagination-${this.size}` : ""
      return `pagination ${sizeClass} justify-content-${this.align}`
    },
    showDots() {
      return this.dots && this.limit > 4 && this.limit < this.pages
    },
    maxPrevItems() {
      return Math.floor((this.limit - 1) / 2)
    },
    maxNextItems() {
      return Math.ceil((this.limit - 1) / 2)
    },
    beforeDots() {
      return this.showDots && this.activePage > this.maxPrevItems + 1
    },
    afterDots() {
      return this.showDots && this.activePage < this.pages - this.maxNextItems
    },
    computedLimit() {
      return this.limit - this.afterDots - this.beforeDots
    },
    range() {
      return this.activePage + this.maxNextItems
    },
    lastItem() {
      return this.range >= this.pages ? this.pages : this.range - this.afterDots
    },
    itemsAmount() {
      return this.pages < this.computedLimit ? this.pages : this.computedLimit
    },
    items() {
      if (this.activePage - this.maxPrevItems <= 1) {
        return Array.from({ length: this.itemsAmount }, (v, i) => i + 1)
      } else {
        return Array.from({ length: this.itemsAmount }, (v, i) => {
          return this.lastItem - i
        }).reverse()
      }
    }
  },
  watch: {
    activePage: {
      immediate: true,
      handler(val) {
        this.liveMessage = `Page ${val || 1} selected`
      }
    },
    pages: {
      immediate: true,
      handler(val) {
        if (val && val < this.activePage) {
          this.$emit("update:activePage", val, true)
          this.$emit("update:modelValue", val)
        }
      }
    }
  },
  methods: {
    getPageAriaLabel(pageNumber) {
      return this.activePage === pageNumber
        ? `Current page ${pageNumber}`
        : `Go to page ${pageNumber}`
    },
    setPage(number, shouldFocus = false) {
      const pageNumber = Number(number)
      if (
        Number.isNaN(pageNumber) ||
        pageNumber < 1 ||
        pageNumber > this.pages ||
        pageNumber === this.activePage
      ) {
        return
      }
      this.$emit("update:activePage", pageNumber, false)
      this.$emit("update:modelValue", pageNumber)
      if (shouldFocus) {
        this.changeFocus(pageNumber)
      }
    },
    changeFocus(pageNumber) {
      this.$nextTick(() => {
        const targetButton = this.$el?.querySelector(
          `.c-page-link-number[data-page="${pageNumber}"]`
        )
        targetButton?.focus()
      })
    }
  }
}
</script>
